import type { CustomerSummary } from '@contract';
import { Link01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import type { ReactNode } from 'react';
import {
  type SheetRef,
  useSheetActions,
  useSheetScope,
} from '@/components/sheet/use-sheet';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { sheetKey } from '@/lib/sheet-url';
import { TONE_CLASSES, type Tone } from '@/lib/status';
import { cn } from '@/lib/utils';

export interface SummaryCell {
  label: string;
  /** Missing data shows a dash, never a zero. */
  value: number | string | null | undefined;
  tone?: Tone;
  /** Makes the cell a filter: the figure is a button. */
  onSelect?: () => void;
  active?: boolean;
}

/**
 * The figures above a queue as one bordered strip (§9.4 to §9.6), not a card
 * per number. A cell with `onSelect` filters the list below it. A tone
 * colours the figure only when the figure is non-zero.
 */
export function SummaryBand({
  cells,
  label,
}: Readonly<{ cells: SummaryCell[]; label: string }>) {
  return (
    <dl
      aria-label={label}
      className="grid grid-cols-2 divide-x divide-y divide-border overflow-hidden rounded-lg border border-border bg-card sm:flex sm:divide-y-0"
    >
      {cells.map((cell) => {
        const missing = cell.value === null || cell.value === undefined;
        const loud =
          !missing && cell.tone && cell.tone !== 'neutral' && cell.value !== 0;
        const body = (
          <>
            <dt className="text-xs text-muted-foreground">{cell.label}</dt>
            <dd
              className={cn(
                'text-xl font-semibold tabular-nums',
                loud && cell.tone ? TONE_CLASSES[cell.tone].ink : null,
              )}
            >
              {missing ? '—' : cell.value}
            </dd>
          </>
        );
        const base =
          'flex min-w-0 flex-1 flex-col gap-0.5 px-4 py-2.5 text-left';
        return cell.onSelect ? (
          <button
            key={cell.label}
            type="button"
            aria-pressed={cell.active}
            onClick={cell.onSelect}
            className={cn(
              base,
              'transition-colors hover:bg-row-hover focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
              cell.active && 'bg-accent',
            )}
          >
            {body}
          </button>
        ) : (
          <div key={cell.label} className={base}>
            {body}
          </div>
        );
      })}
    </dl>
  );
}

/** "Kofi A." gives "KA"; one word gives its first letter; nothing gives "?". */
export function initials(name: string | null | undefined): string {
  const words = (name ?? '').split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words.at(-1)?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

/** Avatar, name and the masked phone: the first cell of every people list. */
export function PersonCell({
  customer,
}: Readonly<{ customer: CustomerSummary | null | undefined }>) {
  if (!customer)
    return <span className="text-muted-foreground">No customer</span>;
  return (
    <span className="flex min-w-0 items-center gap-2">
      <Avatar size="sm">
        <AvatarFallback>{initials(customer.name)}</AvatarFallback>
      </Avatar>
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="truncate font-medium">
          {customer.name ?? 'No name yet'}
        </span>
        <span className="truncate text-xs text-muted-foreground tabular-nums">
          {customer.phone ?? 'No phone'}
        </span>
      </span>
    </span>
  );
}

/**
 * A control that opens a record's sheet. It carries `data-sheet-origin`, so
 * closing the sheet returns focus to this row even after a refresh redraws it.
 */
export function OpenRecord({
  sheetRef,
  label,
  className,
  children,
}: Readonly<{
  sheetRef: SheetRef;
  /** The accessible name: what opening does, in words. */
  label: string;
  className?: string;
  children: ReactNode;
}>) {
  const sheet = useSheetActions();
  // Inside a sheet a record link pushes; on a page it opens.
  const inSheet = useSheetScope() !== null;
  return (
    <button
      type="button"
      aria-label={label}
      data-sheet-origin={sheetKey(sheetRef)}
      onClick={(event) =>
        inSheet
          ? sheet.push(sheetRef)
          : sheet.open(sheetRef, { fromRowEl: event.currentTarget })
      }
      className="group/record inline-flex min-w-0 max-w-full items-center gap-1 rounded-xs text-left outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
    >
      <span className={cn('min-w-0', className)}>{children}</span>
      {/* Marks it as a link to another record. */}
      <HugeiconsIcon
        icon={Link01Icon}
        size={13}
        aria-hidden
        className="shrink-0 text-muted-foreground group-hover/record:text-foreground"
      />
    </button>
  );
}
